import { Dropdown, DropdownOption, LoadingSpinner } from '@yembo/yemblocks-core';
import { Dispatch, useEffect, useState } from 'react';
import { useAllBasicEmployeesQuery } from 'src/queries/Employee';
import { useAllAddContactCompaniesQuery } from 'src/queries/Company';
import { ContactJobState, CreateContactFormData } from 'src/react/Contact';
import { getFullName } from 'src/react/_util/getFullName';
import { AddContactCompany } from 'src/types';
import { disableJobCreationCompanyKeys } from '../_shared';

type Props = {
  formData: CreateContactFormData;
  dispatchFormData: Dispatch<Partial<CreateContactFormData>>;
  dispatchJobState: Dispatch<Partial<ContactJobState>>;
};

export const EmployeeSection = ({ dispatchFormData, formData, dispatchJobState }: Props) => {
  const { origin, destination, company, employee, validation } = formData;

  const [shouldLoadEmployees, setShouldLoadEmployees] = useState(false);
  const [shouldLoadCompanies, setShouldLoadCompanies] = useState(false);

  const { key: companyKey, name: companyName, parentCompanyKey } = company;
  const { key: employeeKey } = employee ?? {};

  const { data: allEmployees } = useAllBasicEmployeesQuery({
    enabled: shouldLoadEmployees,
  });

  const forceToSelectCompany =
    disableJobCreationCompanyKeys.has(companyKey) ||
    (parentCompanyKey && disableJobCreationCompanyKeys.has(parentCompanyKey));

  const { data: companies } = useAllAddContactCompaniesQuery({
    enabled: shouldLoadCompanies,
  });
  const [companiesToChooseFrom, setCompaniesToChooseFrom] = useState<AddContactCompany[] | undefined>(
    forceToSelectCompany ? undefined : companies
  );

  useEffect(() => {
    if (forceToSelectCompany && (!shouldLoadCompanies || !companies)) {
      if (validation.isCompanySelected)
        dispatchFormData({
          validation: { ...validation, isCompanySelected: false },
        });
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCompaniesToChooseFrom(companies);
  }, [forceToSelectCompany, companies, shouldLoadCompanies, dispatchFormData, validation]);

  const employees = allEmployees?.filter(({ companyKeys }) => companyKeys?.includes(companyKey) ?? false);

  const getEmployeesOptions = (): DropdownOption[] => {
    if (!employees) return loadingOption;

    return employees
      .sort((a, b) => (a.givenName ?? '').localeCompare(b.givenName ?? ''))
      .map((optionEmployee) => ({
        key: optionEmployee.key,
        name: getFullName(optionEmployee),
        isSelected: optionEmployee.key === employeeKey,
        onClick: () => dispatchFormData({ employee: optionEmployee }),
      }));
  };

  const getCompaniesOptions = (): DropdownOption[] => {
    if (!companiesToChooseFrom) return loadingOption;

    return companiesToChooseFrom
      .filter((c) => !disableJobCreationCompanyKeys.has(c.key))
      .map((optionCompany) => ({
        key: optionCompany.key,
        name: optionCompany.name,
        isSelected: optionCompany.key === companyKey && (!forceToSelectCompany || validation.isCompanySelected),
        onClick: () => {
          dispatchFormData({
            company: optionCompany,
            origin: { ...origin, country: optionCompany.defaultCountry },
            destination: {
              ...destination,
              country: optionCompany.defaultCountry,
            },
            validation: { ...validation, isCompanySelected: true },
          });
          dispatchJobState({ locale: optionCompany.defaultLocale });
        },
      }));
  };

  return (
    <div className='section employee-section'>
      <p className='bold header'>Adjuster</p>
      <div className='dropdown-section column'>
        <Dropdown
          label='Company'
          enableSearch
          options={getCompaniesOptions()}
          customValue={forceToSelectCompany ? undefined : companyName}
          onOpenCallback={() => {
            setShouldLoadCompanies(true);
            if (companies) setCompaniesToChooseFrom(companies);
          }}
          isRequired
          shouldDisableAutocomplete
        />
        <Dropdown
          label='Assign to'
          enableSearch
          options={getEmployeesOptions()}
          customValue={employee ? `${employee.givenName} ${employee.familyName}` : ''}
          onOpenCallback={() => setShouldLoadEmployees(true)}
        />
      </div>
    </div>
  );
};

const loadingOption = [
  {
    key: 'loading',
    name: 'Loading...',
    optionItem: <LoadingSpinner label='Loading...' />,
    isSelected: false,
    className: 'no-pointer-events',
  },
];
